/**
 * useListingSaving Composable
 * Centralizes save/unsave listing logic
 */

import { ref } from 'vue';
import { apiPost } from '@shared/utils';

/**
 * Composable for managing listing save/unsave operations
 * @returns {Object} Listing saving state and methods
 * @example
 * const { isSaving, toggleSaved } = useListingSaving()
 * 
 * const handleSave = async (listingId) => {
 *   const result = await toggleSaved(listingId)
 *   if (result.success) {
 *     // Handle success
 *   }
 * }
 */
export function useListingSaving() {
  const isSaving = ref(false);

  /**
   * Toggle saved status of a listing
   * @param {number} listingId - ID of the listing to toggle
   * @returns {Promise<Object>} Result object with success and ownershipState
   * @throws {Error} If the API call fails
   */
  const toggleSaved = async (listingId) => {
    if (isSaving.value) {
      throw new Error('Already saving');
    }

    isSaving.value = true;

    try {
      const data = await apiPost('/api/listing/toggle-favorite', {
        listingId: listingId
      });

      if (!data.success) {
        throw new Error(data.error || 'Failed to toggle saved status');
      }

      return data;
    } catch (error) {
      console.error('Error toggling saved status:', error);
      throw error;
    } finally {
      isSaving.value = false;
    }
  };

  /**
   * Save a listing (if not already saved)
   * @param {number} listingId - ID of the listing to save
   * @returns {Promise<Object>} Result object
   */
  const saveListing = async (listingId) => {
    const result = await toggleSaved(listingId);
    
    // If result shows it was already saved, return success anyway
    if (result.ownershipState === 'Saved') {
      return result;
    }
    
    // If it wasn't saved, the toggle should have saved it
    return result;
  };

  /**
   * Unsave a listing (if currently saved)
   * @param {number} listingId - ID of the listing to unsave
   * @returns {Promise<Object>} Result object
   */
  const unsaveListing = async (listingId) => {
    const result = await toggleSaved(listingId);
    
    // If result shows it was already unsaved, return success anyway
    if (result.ownershipState === 'Unsaved') {
      return result;
    }
    
    // If it was saved, the toggle should have unsaved it
    return result;
  };

  return {
    // State
    isSaving,

    // Methods
    toggleSaved,
    saveListing,
    unsaveListing
  };
}
